import * as React from 'react';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { withRouter } from 'react-router';

import { push } from 'connected-react-router';

import NewProjectRouter from './NewProjectRouter';
import ProjectLaunch from './tab/projectLaunch';
import { IApplicationState } from '../../reducers';
import InfoPopup from '../common/InfoPopup';
import { ErrorPopupContent, WarningPopupContent } from '../common/Popups';
import {
    fetchProjects,
    onPause,
    onLaunch,
    applyProjectLock,
    updateProjectLockUser,
    launchSandbox
} from '../../services/project.service';
import {
    PROJECT_STATUS,
    HealthProblem,
    HealthProblemContext,
    HealthProblemType,
    VIEW_MODE,
    PRICE_PROCESS_TYPES,
    QuestionSummary,
    PROJECT_EDIT_STATUS
} from '@nexxt/common/types';
import { interpolate, splitPathname } from '../../utils/string';

import { alertMessage, titleHeader } from '../../constants/alertMessage';
import {
    clearProjects,
    setProject,
    setEditEnabled
} from '../../actions/project.actions';
import { sortHealthProblemByQuestionID } from '../../utils/project';
import { LAUNCH_PROJECT, CHECKOUT } from '../../constants/routes';
import { getConfirmationBox } from '../common/swal/Sweetalert';
import PriceModal from './tab/PriceModal';
import { getPrice } from '../../services/payment.service';
import { PROJECT_EDIT_MODES } from '../../interface/project.interface';
import { Project } from '@nexxt/common/types';
import {
    getSortedQuestionByProjectId,
    saveUpdatedSortedQuestion
} from '../../services/question.service';
import * as i18next from 'i18next';
import ProjectLockPopup from '../common/Popups/ProjectLockPopup';
import { ILocation } from '../../interface/common.interface';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';

interface NewProjectProps {
    questionSummaries: QuestionSummary[];
    isNewProjectCreated: boolean;
    currentProject: any;
    tenant: any;
    isEdited: boolean;
    packageID: number;
    projects: any[];
    projectHealth: any;
    history: any;
    location: any;
    priceType: any;
    viewMode: string;
    editMode: PROJECT_EDIT_MODES;
    currentLockUser: {
        userId: number;
        firstName: string;
        lastName: string;
        email: string;
        isValid: boolean;
    };
    editStatus: PROJECT_EDIT_STATUS;
    fetchProjects: () => Promise<any>;
    getSortedQuestionByProjectId: (projectId: number) => Promise<any>;
    getPrice: (data) => Promise<any>;
    onPause: (projectId: number, status: PROJECT_STATUS) => Promise<any>;
    onLaunch: (
        projectId: number,
        packageID: number,
        codeName: string
    ) => Promise<any>;
    clearProjects: () => void;
    changePath: (path: string | ILocation) => void;
    setProject: (project: Project) => Promise<any>;
    applyProjectLock: (projectId: number) => Promise<any>;
    updateProjectLockUser: (projectId: number, data: any) => Promise<any>;
    setEditEnabled: (toggle: boolean) => void;
    saveUpdatedSortedQuestion: (projectId: number) => void;
    toastNotification: any;
    launchSandbox: (projectID: number) => Promise<void>;
}
let pricePollingInterval = null;
let permissionPollingInterval = null;
const NewProject: React.SFC<NewProjectProps> = (props: NewProjectProps) => {
    const [exit, setExit] = React.useState(false);
    const [showPriceModal, setShowPriceModal] = React.useState(false);
    const [showErrorPopup, setShowErrorPopup] = React.useState(false);
    const [showWarningPopup, setShowWarningPopup] = React.useState(false);
    const [showErrorPopupWindow, setShowErrorPopupWindow] =
        React.useState(false);
    const [showErrorPopupWindowFooter, setShowErrorPopupWindowFooter] =
        React.useState(true);
    const [showWarningPopupWindow, setShowWarningPopupWindow] =
        React.useState(false);
    const [showWarningPopupWindowFooter, setShowWarningPopupWindowFooter] =
        React.useState(true);
    const [showPricingPopupModal, setShowPricingPopupModal] =
        React.useState(false);
    const [showProjectLockPopup, setShowProjectLockPopup] =
        React.useState(false);
    const totalQuestionCount = props.questionSummaries?.length;

    // price polling every 10 seconds
    const pollingUpdatePrice = async () => {
        pricePollingInterval = setInterval(async () => {
            await props.getPrice({
                projectID: props.currentProject.id
            });
        }, 10000);
    };
    const resetProjectLock = () => {
        props.updateProjectLockUser(props.currentProject.id, {
            mode: 'RESET'
        });
    };

    /*
     * Polls the project edit permission endpoint every 5 seconds, continuously applying the latest lock user to state.
     * Only needs to be called once per session that a project is loaded.
     * Also sets an event listener which will cause the permission lock to be reset before unload.
     *
     * @TODO: discuss whether the reset request before unload is the best strategy.
     * For example, it could be better to add a timestamp column in database and to
     * use that to check if project was recently locked (if it is more than 5 seconds ago)
     * we can assume that the user either left or lost their connection.
     */
    const pollingProjectLock = async () => {
        permissionPollingInterval = setInterval(
            (function polling() {
                props.applyProjectLock(props.currentProject.id);
                return polling;
            })(),
            5000
        );
        window.addEventListener('beforeunload', resetProjectLock);
    };

    // Start getting price polling
    // Only check when priceType.status gets updated
    // The polling will be only invoked when status returns with 'PROCESSING'
    // Once the status get updated to 'COMPLETE' from 'PROCESSING', pricePolling will be reset.
    React.useEffect(() => {
        (async () => {
            if (props.priceType?.status === PRICE_PROCESS_TYPES.COMPLETE) {
                pricePollingInterval && clearInterval(pricePollingInterval);
            }
            if (props.priceType?.status === PRICE_PROCESS_TYPES.PROCESSING)
                await pollingUpdatePrice();
        })();
    }, [props.priceType?.status]);

    React.useEffect(() => {
        if (props.currentProject.id) {
            pollingProjectLock();

            return () => {
                resetProjectLock();
                window.removeEventListener('beforeunload', resetProjectLock);
            };
        }
    }, [props.currentProject?.id]);

    React.useEffect(() => {
        setShowProjectLockPopup(!props.currentLockUser?.isValid);
        if (props.currentLockUser.isValid && props.currentLockUser.userId) {
            const projectId = splitPathname(props.location.pathname);
            if (projectId !== 'new' && !props.questionSummaries?.length) {
                props.getSortedQuestionByProjectId(projectId);
            }
        }
    }, [props.currentLockUser]);

    React.useEffect(() => {
        return () => {
            !window.location.pathname.includes('/launch') &&
                props.clearProjects();
            // keep DEV mode for question tabs only
            pricePollingInterval && clearInterval(pricePollingInterval);
            permissionPollingInterval &&
                clearInterval(permissionPollingInterval);
        };
    }, []);

    React.useEffect(() => {
        if (!props.projects.length) {
            props.fetchProjects();
        }
    }, []);

    React.useEffect(() => {
        if (props.currentProject?.languages) {
            const languageValues = props.currentProject.languages.map(
                (l) => l.value
            );
            // loads translation files when the project languages are changed
            i18next.loadLanguages(languageValues, (err) => {});
        }
    }, [props.currentProject?.languages]);

    async function confirmUserProceed(nextLocation) {
        const confirm = await getConfirmationBox({
            message: alertMessage.CONFIRM_CONTINUE,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.UNDO_CURRENT_CHANGE
        });
        if (confirm.isConfirmed) {
            setExit(true);
            props.history.push(nextLocation);
        }
    }

    const projectHealthErrors = Object.values(props.projectHealth)
        .filter((problem) => (problem as any).type === HealthProblemType.ERROR)
        .flatMap((questionProblem) =>
            (questionProblem as any).list
                .filter(
                    (problem) =>
                        (problem as HealthProblem).problemType ===
                        HealthProblemType.ERROR
                )
                .map((problem) => (problem as HealthProblem).message)
        );

    const projectHealthWarnings = Object.values(props.projectHealth)
        .filter(
            (problem) => (problem as any).type === HealthProblemType.WARNING
        )
        .flatMap((questionProblem) =>
            (questionProblem as any).list
                .filter(
                    (problem) =>
                        (problem as HealthProblem).problemType ===
                        HealthProblemType.WARNING
                )
                .map((problem) => (problem as HealthProblem).message)
        );

    const ErrorPopup = (
        <InfoPopup
            show={showErrorPopup}
            icon={`${STATIC_STORAGE_URL}/img/svg/error-icon.svg`}
            title="Errors"
            content={
                <ErrorPopupContent
                    problems={projectHealthErrors}
                    close={() => setShowErrorPopup(false)}
                />
            }
            close={() => setShowErrorPopup(false)}
        />
    );

    const WarningPopup = (
        <InfoPopup
            show={showWarningPopup}
            icon={`${STATIC_STORAGE_URL}/img/svg/warning-icon.svg`}
            title="Suggestions"
            content={
                <WarningPopupContent
                    problems={projectHealthWarnings}
                    close={() => setShowWarningPopup(false)}
                />
            }
            close={() => setShowWarningPopup(false)}
        />
    );

    const closeErrWindowBtn = (
        <button
            className="btn btn--sm btn--green"
            onClick={() => setShowErrorPopupWindow(false)}
        >
            Back to Editor
        </button>
    );

    const ErrorPopupWindow = (
        <InfoPopup
            full
            sidebarBackgroundColor="#E76E3C"
            sidebarTitle="Almost ready to launch"
            sidebarText=""
            sidebarIcon={`${STATIC_STORAGE_URL}/img/svg/lg-error-icon.svg`}
            show={showErrorPopupWindow}
            content={
                <ErrorPopupContent
                    problems={projectHealthErrors}
                    close={() => {
                        setShowErrorPopupWindowFooter(true);
                        setShowErrorPopupWindow(false);
                    }}
                    hideFooter={() => setShowErrorPopupWindowFooter(false)}
                />
            }
            footer={showErrorPopupWindowFooter ? closeErrWindowBtn : null}
            close={() => {
                setShowErrorPopupWindowFooter(true);
                setShowErrorPopupWindow(false);
            }}
        />
    );

    const launchProject = async () => {
        if (props.packageID) {
            const response = await props.onLaunch(
                props.currentProject.id,
                props.packageID,
                props.currentProject.codeName || props.currentProject.liveURL
            );
            if (response.status === 200) {
                props.changePath(
                    interpolate(LAUNCH_PROJECT, {
                        projectId: props.currentProject.id
                    })
                );
            }
        } else {
            props.changePath({
                pathname: interpolate(CHECKOUT, {
                    projectId: props.currentProject.id
                }),
                search: props.viewMode === VIEW_MODE.DEV ? '?mode=dev' : ''
            } as ILocation);
        }
    };
    const forceOpenCheckout = () => {
        setShowPriceModal(false);
        setShowPricingPopupModal(true);
    };

    const closeWarningWindowBtn = (
        <>
            <button
                className="btn btn--sm btn--yellow"
                onClick={() => setShowWarningPopupWindow(false)}
            >
                Back
            </button>
            <button className="btn btn--sm btn--green" onClick={launchProject}>
                Publish Anyways
            </button>
        </>
    );

    const WarningPopupWindow = (
        <InfoPopup
            full
            sidebarBackgroundColor="#595959"
            sidebarTitle="Just so you know,"
            sidebarText="There may be some errors with the way the questions were formed."
            sidebarIcon={`${STATIC_STORAGE_URL}/img/svg/three-dot-icon.svg`}
            show={showWarningPopupWindow}
            content={
                <WarningPopupContent
                    problems={projectHealthWarnings}
                    close={() => {
                        setShowWarningPopupWindowFooter(true);
                        setShowWarningPopupWindow(false);
                    }}
                    hideFooter={() => setShowWarningPopupWindowFooter(false)}
                />
            }
            footer={showWarningPopupWindowFooter ? closeWarningWindowBtn : null}
            close={() => {
                setShowWarningPopupWindowFooter(true);
                setShowWarningPopupWindow(false);
            }}
        />
    );
    const PricingPopupWindow = (
        <InfoPopup
            full
            sidebarBackgroundColor="#349A71"
            sidebarTitle="Pick a pricing option"
            sidebarText=""
            sidebarIcon={`${STATIC_STORAGE_URL}/img/svg/price-tag.svg`}
            show={showPricingPopupModal}
            content={
                <PriceModal
                    onClose={() => setShowPricingPopupModal(false)}
                    changePath={props.changePath}
                    currentProject={props.currentProject}
                    type={props.priceType}
                    getPrice={props.getPrice}
                    contentOnly={true}
                    setProject={props.setProject}
                    devMode={props.viewMode === VIEW_MODE.DEV}
                    totalQuestionCount={totalQuestionCount}
                />
            }
            footer={null}
            close={() => {
                setShowPricingPopupModal(false);
            }}
        />
    );

    const launchSandbox = async () => {
        const res = await getConfirmationBox({
            title: titleHeader.REVIEW_PROJECT,
            message: alertMessage.CONFIRM_LAUNCH_REVIEW,
            showCloseButton: true
        });
        if (res.isConfirmed) {
            const projectId = props.currentProject.id;
            // call api to update proj status to under review
            await props.launchSandbox(projectId);
            props.changePath(
                interpolate(LAUNCH_PROJECT, {
                    projectId
                })
            );
        }
    };
    return (
        <React.Fragment>
            <ProjectLockPopup
                projectId={props.currentProject?.id}
                show={showProjectLockPopup}
                currentLockUser={props.currentLockUser}
                changePath={props.changePath}
                setEditEnabled={props.setEditEnabled}
                onClose={() => setShowProjectLockPopup(false)}
                updateProjectLockUser={props.updateProjectLockUser}
            />
            {showPriceModal && (
                <PriceModal
                    onClose={() => setShowPriceModal(false)}
                    changePath={props.changePath}
                    currentProject={props.currentProject}
                    type={props.priceType}
                    getPrice={props.getPrice}
                    contentOnly={false}
                    setProject={props.setProject}
                    forceOpenCheckout={forceOpenCheckout}
                    devMode={props.viewMode === VIEW_MODE.DEV}
                    launchSandbox={launchSandbox}
                    totalQuestionCount={totalQuestionCount}
                />
            )}
            <div className="project-add--container">
                <div className="project-add--wrapper">
                    <Prompt
                        when={props.isEdited}
                        // message={alertMessage.UNDO_CURRENT_CHANGE}
                        message={(nextLocation) => {
                            if (exit) {
                                setExit(false);
                                return true;
                            } else {
                                confirmUserProceed(nextLocation);
                                return false;
                            }
                        }}
                    />
                    {/* <NewProjectTab /> */}
                    {ErrorPopup}
                    {WarningPopup}
                    {ErrorPopupWindow}
                    {WarningPopupWindow}
                    {PricingPopupWindow}
                    <NewProjectRouter />
                    {/* {(props.currentProject.status !== PROJECT_STATUS.LIVE ||
                        props.viewMode === VIEW_MODE.DEV) && (
                        <NewProjectRouter />
                    )} */}
                    {props.currentProject && props.currentProject.id && (
                        <ProjectLaunch
                            location={props.location}
                            showProjectLockPopup={() =>
                                setShowProjectLockPopup(true)
                            }
                            projectId={props.currentProject.id}
                            status={
                                props.currentProject &&
                                props.currentProject.status
                            }
                            devMode={props.viewMode === VIEW_MODE.DEV}
                            isEdited={props.isEdited}
                            onLaunch={props.onLaunch}
                            onPause={props.onPause}
                            currentProject={props.currentProject}
                            codeName={
                                props.currentProject.codeName ||
                                props.currentProject.liveURL
                            }
                            priceType={props.priceType}
                            packageID={props.packageID}
                            tenantId={props.tenant.tenantID}
                            tenantURL={props.tenant.tenantURL}
                            changePath={props.changePath}
                            projectHealth={props.projectHealth}
                            editMode={props.editMode}
                            onOpenPriceModal={() => setShowPriceModal(true)}
                            showPricingPopup={() =>
                                setShowPricingPopupModal(true)
                            }
                            showErrorPopup={() => setShowErrorPopup(true)}
                            showWarningPopup={() => setShowWarningPopup(true)}
                            showErrorPopupWindow={() =>
                                setShowErrorPopupWindow(true)
                            }
                            showWarningPopupWindow={() =>
                                setShowWarningPopupWindow(true)
                            }
                            editStatus={props.editStatus}
                            launchSandbox={launchSandbox}
                            totalQuestionCount={totalQuestionCount}
                        />
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};
const mapStateToProps = (store: IApplicationState) => {
    const projectId = splitPathname(store.router.location.pathname);

    const {
        projectReducer: {
            isNewProjectCreated,
            currentProject,
            packageID,
            projects,
            projectHealth,
            priceType,
            editMode,
            currentLockUser
        },
        questionReducer: { questionSummaries },
        utilsReducer: { isEdited, viewMode, editStatus },
        authReducer: { tenant }
    } = store;
    return {
        currentProject,
        priceType,
        projects,
        isNewProjectCreated,
        isEdited,
        packageID,
        tenant,
        viewMode,
        editMode,
        currentLockUser,
        projectHealth: sortHealthProblemByQuestionID(
            projectHealth,
            [
                HealthProblemContext.CONDITION,
                HealthProblemContext.QUESTION,
                HealthProblemContext.AUDIENCE,
                HealthProblemContext.OVERVIEW
            ],
            [HealthProblemType.ERROR, HealthProblemType.WARNING]
        ),
        questionSummaries: questionSummaries[projectId],
        editStatus
    };
};

const mapDispatchToProps = (dispatch) => ({
    fetchProjects: () => dispatch(fetchProjects()),
    getSortedQuestionByProjectId: (projectId: number) =>
        dispatch(getSortedQuestionByProjectId(projectId)),
    saveUpdatedSortedQuestion: (projectId: number) =>
        dispatch(saveUpdatedSortedQuestion(projectId)),
    onLaunch: (projectId: number, packageID: number, codeName: string) =>
        dispatch(onLaunch(projectId, packageID, codeName)),
    onPause: (projectId: number, status: PROJECT_STATUS) =>
        dispatch(onPause(projectId, status)),
    clearProjects: () => {
        dispatch(clearProjects());
    },
    setEditEnabled: (enabled: boolean) => {
        dispatch(setEditEnabled(enabled));
    },
    changePath: (path) => {
        dispatch(push(path));
    },
    getPrice: (data) => {
        dispatch(getPrice(data));
    },
    setProject: (project) => {
        dispatch(setProject(project));
    },
    applyProjectLock: (projectID: number) =>
        dispatch(applyProjectLock(projectID)),
    updateProjectLockUser: (projectID: number, data: any) =>
        dispatch(updateProjectLockUser(projectID, data)),
    launchSandbox: (projectID: number) => dispatch(launchSandbox(projectID))
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(NewProject)
);
